// frontend/src/pages/admin/AdminUsersPage.jsx
import { useState, useEffect } from 'react';
import { 
  Edit, Trash2, Loader2, Search, RefreshCw, 
  ArrowLeft, User, Phone, Mail, Shield, Calendar, CheckCircle, 
  XCircle, Save, AlertCircle, Lock, Unlock, KeyRound
} from 'lucide-react';
import { PageSEO } from '../../components/SEO/PageSEO';
import config from '../../config';

// کامپوننت صفحه ویرایش کاربر
const UserEditPage = ({ userId, onBack, onSave }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    role: 'customer',
    is_active: true,
  });
  
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    new_password: '',
    confirm_password: '',
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState(null);

  useEffect(() => {
    fetchUser();
  }, [userId]);

  const fetchUser = async () => {
    setLoading(true);
    try {
      const accessToken = localStorage.getItem('access_token');
      const response = await fetch(config.getApiUrl(`/accounts/users/${userId}/`), {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('خطا در دریافت اطلاعات کاربر');
      
      const data = await response.json();
      setUser(data);
      setFormData({
        username: data.username || '',
        email: data.email || '',
        role: data.role || 'customer',
        is_active: data.is_active !== undefined ? data.is_active : true,
      });
    } catch (error) {
      console.error('Error fetching user:', error);
      setError('خطا در دریافت اطلاعات کاربر');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    
    try {
      const accessToken = localStorage.getItem('access_token');
      const response = await fetch(config.getApiUrl(`/accounts/users/${userId}/`), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: formData.username,
          email: formData.email,
          role: formData.role,
          is_active: formData.is_active,
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'خطا در ذخیره تغییرات');
      }
      
      alert('اطلاعات کاربر با موفقیت به‌روزرسانی شد');
      if (onSave) onSave();
      if (onBack) onBack();
    } catch (error) {
      console.error('Error saving user:', error);
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setChangingPassword(true);
    setPasswordError(null);
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordError('رمز عبور و تکرار آن مطابقت ندارد');
      setChangingPassword(false);
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      setPasswordError('رمز عبور باید حداقل 6 کاراکتر باشد');
      setChangingPassword(false);
      return;
    }
    
    try {
      const accessToken = localStorage.getItem('access_token');
      const response = await fetch(config.getApiUrl(`/accounts/users/${userId}/change-password/`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          new_password: passwordData.new_password,
          confirm_password: passwordData.confirm_password,
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'خطا در تغییر رمز عبور');
      }
      
      alert('رمز عبور با موفقیت تغییر کرد');
      setShowPasswordModal(false);
      setPasswordData({ new_password: '', confirm_password: '' });
    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordError(error.message);
    } finally {
      setChangingPassword(false);
    }
  };

  const getRoleOptions = () => {
    return [
      { value: 'customer', label: 'مشتری', color: 'bg-gray-100 text-gray-800' },
      { value: 'seller', label: 'فروشنده', color: 'bg-blue-100 text-blue-800' },
      { value: 'installer', label: 'نصاب', color: 'bg-green-100 text-green-800' },
      { value: 'admin', label: 'ادمین', color: 'bg-purple-100 text-purple-800' },
    ];
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <h3 className="text-lg font-medium text-red-800 mb-2">خطا در بارگذاری</h3>
        <p className="text-red-600 mb-4">{error}</p>
        <button onClick={onBack} className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">
          بازگشت
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition"
      >
        <ArrowLeft className="w-5 h-5" />
        بازگشت به لیست کاربران
      </button>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">ویرایش کاربر</h1>
                <p className="text-blue-100 text-sm">شناسه: {user?.id}</p>
              </div>
            </div>
            <button
              onClick={() => setShowPasswordModal(true)}
              className="flex items-center gap-2 px-3 py-1.5 bg-white/20 text-white rounded-lg hover:bg-white/30 transition"
            >
              <KeyRound className="w-4 h-4" />
              تغییر رمز عبور
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <User className="w-4 h-4 inline ml-1 text-gray-400" />
              نام کاربری
            </label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Phone className="w-4 h-4 inline ml-1 text-gray-400" />
              شماره تماس
            </label>
            <input
              type="tel"
              value={user?.phone || '-'}
              className="w-full border rounded-lg px-3 py-2 bg-gray-50 text-gray-500"
              disabled
            />
            <p className="text-xs text-gray-400 mt-1">شماره تماس قابل تغییر نیست</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Mail className="w-4 h-4 inline ml-1 text-gray-400" />
              ایمیل
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="example@domain.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Shield className="w-4 h-4 inline ml-1 text-gray-400" />
              نقش کاربری
            </label>
            <div className="grid grid-cols-2 gap-3">
              {getRoleOptions().map((role) => (
                <label
                  key={role.value}
                  className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition ${
                    formData.role === role.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="role"
                    value={role.value}
                    checked={formData.role === role.value}
                    onChange={handleChange}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className={`px-2 py-0.5 text-xs rounded-full ${role.color}`}>
                    {role.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="font-medium text-gray-900">وضعیت حساب کاربری</label>
                <p className="text-sm text-gray-500">
                  کاربران غیرفعال نمی‌توانند وارد سامانه شوند
                </p>
              </div>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, is_active: !prev.is_active }))}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                  formData.is_active
                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                    : 'bg-red-100 text-red-700 hover:bg-red-200'
                }`}
              >
                {formData.is_active ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    فعال
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4" />
                    غیرفعال
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <h3 className="font-medium text-gray-900 mb-2">اطلاعات اضافی</h3>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">تاریخ عضویت:</span>
              <span className="text-gray-900">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString('fa-IR') : '-'}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">آخرین ورود:</span>
              <span className="text-gray-900">
                {user?.last_login ? new Date(user.last_login).toLocaleDateString('fa-IR') : '---'}
              </span>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onBack}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50 transition"
            >
              انصراف
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              ذخیره تغییرات
            </button>
          </div>
        </form>
      </div>

      {/* مودال تغییر رمز عبور */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center p-6 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-purple-600" />
                <h2 className="text-xl font-bold text-gray-900">تغییر رمز عبور</h2>
              </div>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordError(null);
                  setPasswordData({ new_password: '', confirm_password: '' });
                }}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleChangePassword} className="p-6 space-y-4">
              {passwordError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {passwordError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  رمز عبور جدید
                </label>
                <input
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="حداقل 6 کاراکتر"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  تکرار رمز عبور جدید
                </label>
                <input
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="تکرار رمز عبور جدید"
                  required
                />
              </div>

              <div className="bg-yellow-50 rounded-lg p-3 text-sm text-yellow-800">
                <p className="font-medium mb-1">نکته مهم:</p>
                <p>پس از تغییر رمز عبور، کاربر باید با رمز جدید وارد شود.</p>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordError(null);
                    setPasswordData({ new_password: '', confirm_password: '' });
                  }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50 transition"
                >
                  انصراف
                </button>
                <button
                  type="submit"
                  disabled={changingPassword}
                  className="flex items-center gap-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50"
                >
                  {changingPassword ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <KeyRound className="w-4 h-4" />
                  )}
                  تغییر رمز عبور
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// کامپوننت اصلی لیست کاربران
export const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [roleFilter, setRoleFilter] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      fetchUsers();
    }
  }, [currentPage, searchTerm, roleFilter, isEditing]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const accessToken = localStorage.getItem('access_token');
      
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('page_size', 20);
      if (searchTerm) params.append('search', searchTerm);
      if (roleFilter) params.append('role', roleFilter);
      
      const url = config.getApiUrl(`/accounts/users/?${params.toString()}`);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: خطا در دریافت کاربران`);
      }

      const data = await response.json();
      
      setUsers(data.results || []);
      setTotalCount(data.count || 0);
      setTotalPages(Math.ceil((data.count || 0) / 20));
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('خطا در دریافت اطلاعات کاربران');
      setUsers([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    if (!window.confirm(`آیا از ${currentStatus ? 'غیرفعال' : 'فعال'} کردن این کاربر اطمینان دارید؟`)) {
      return;
    }
    
    try {
      const accessToken = localStorage.getItem('access_token');
      const response = await fetch(config.getApiUrl(`/accounts/users/${userId}/`), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !currentStatus })
      });
      
      if (response.ok) {
        alert(`کاربر با موفقیت ${currentStatus ? 'غیرفعال' : 'فعال'} شد`);
        fetchUsers();
      } else {
        const errorData = await response.json();
        alert('خطا: ' + JSON.stringify(errorData));
      }
    } catch (error) {
      console.error('Error toggling user status:', error);
      alert('خطا در تغییر وضعیت کاربر');
    }
  };

  const deleteUser = async (userId, username) => {
    if (!window.confirm(`آیا از حذف کاربر "${username}" اطمینان دارید؟ این عمل غیرقابل بازگشت است.`)) {
      return;
    }
    
    try {
      const accessToken = localStorage.getItem('access_token');
      const response = await fetch(config.getApiUrl(`/accounts/users/${userId}/`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        alert('کاربر با موفقیت حذف شد');
        fetchUsers();
      } else {
        const errorData = await response.json();
        alert('خطا: ' + JSON.stringify(errorData));
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('خطا در حذف کاربر');
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-purple-100 text-purple-800',
      seller: 'bg-blue-100 text-blue-800',
      installer: 'bg-green-100 text-green-800',
      customer: 'bg-gray-100 text-gray-800',
    };
    const labels = {
      admin: 'ادمین',
      seller: 'فروشنده',
      installer: 'نصاب',
      customer: 'مشتری',
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${styles[role] || styles.customer}`}>
        {labels[role] || role}
      </span>
    );
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (isEditing && selectedUserId) {
    return (
      <UserEditPage
        userId={selectedUserId}
        onBack={() => {
          setIsEditing(false);
          setSelectedUserId(null);
        }}
        onSave={() => {
          setIsEditing(false);
          setSelectedUserId(null);
          fetchUsers();
        }}
      />
    );
  }

  if (loading && users.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <>
      <PageSEO title="مدیریت کاربران | پنل ادمین" noindex={true} />
      
      <div>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">مدیریت کاربران</h1>
            <p className="text-sm text-gray-500 mt-1">مدیریت کاربران، تغییر نقش، وضعیت و اطلاعات</p>
          </div>
          <button
            onClick={() => {
              setRefreshing(true);
              fetchUsers();
            }}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 transition"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            بروزرسانی
          </button>
        </div>

        {error && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex flex-wrap gap-4">
            <div className="flex-1 relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="جستجوی کاربر (نام، شماره تماس، ایمیل)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">همه نقش‌ها</option>
              <option value="admin">ادمین</option>
              <option value="seller">فروشنده</option>
              <option value="installer">نصاب</option>
              <option value="customer">مشتری</option>
            </select>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">شناسه</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">نام کاربری</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">شماره تماس</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">ایمیل</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">نقش</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">تاریخ عضویت</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">وضعیت</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">عملیات</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                      کاربری یافت نشد
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50 transition">
                      <td className="px-6 py-4 text-gray-500">{user.id}</td>
                      <td className="px-6 py-4 font-medium text-gray-900">{user.username}</td>
                      <td className="px-6 py-4 text-gray-600">{user.phone || '-'}</td>
                      <td className="px-6 py-4 text-gray-600">{user.email || '-'}</td>
                      <td className="px-6 py-4">{getRoleBadge(user.role)}</td>
                      <td className="px-6 py-4 text-gray-500 text-sm">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString('fa-IR') : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {user.is_active ? 'فعال' : 'غیرفعال'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button 
                            onClick={() => {
                              setSelectedUserId(user.id);
                              setIsEditing(true);
                            }}
                            className="p-1 rounded transition text-blue-600 hover:bg-blue-50"
                            title="ویرایش کاربر"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          <button 
                            onClick={() => toggleUserStatus(user.id, user.is_active)} 
                            className={`p-1 rounded transition ${user.is_active ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}`}
                            title={user.is_active ? 'غیرفعال کردن' : 'فعال کردن'}
                          >
                            {user.is_active ? <Unlock className="w-5 h-5" /> : <Lock className="w-5 h-5" />}
                          </button>
                          <button 
                            onClick={() => deleteUser(user.id, user.username)} 
                            className="p-1 rounded transition text-red-600 hover:bg-red-50"
                            title="حذف کاربر"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-100 flex justify-between items-center">
              <div className="text-sm text-gray-500">
                نمایش {users.length} از {totalCount} کاربر
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                >
                  قبلی
                </button>
                <span className="px-4 py-1 text-sm">
                  صفحه {currentPage} از {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                >
                  بعدی
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};